import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSms, faPaperPlane, faUser, faSearch, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import { getFirestore } from '@firebase/firestore';
import { app } from '../firebase.js';
import { collection, doc, getDocs, orderBy, query, setDoc } from '@firebase/firestore';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import ReactPaginate from 'react-paginate';
import MaskedPhone from '../Components/MaskedPhone';
import { logAdminActivity } from '../utils/activityLogger';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const SMS_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms';
const VERIFY_SMS_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/verifySms/verify-sms';
const SMS_BALANCE_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/smsBalanceCheck/sms-balance';
const LEFTBAR_SMS_BALANCE_KEY = 'leftbarSmsBalanceState';
const LEFTBAR_SMS_BALANCE_EVENT = 'leftbar-sms-balance-update';
const SMS_SENDING_DISABLED = false;
const SMS_GROUP_OPTIONS = [
  { value: 'Student', label: 'Active Students' },
  { value: 'Completed', label: 'Graduated Students' },
  { value: 'Did Not Join', label: 'Never Joined Students' },
  { value: 'Deferred', label: 'Students who Deferred' },
  { value: 'Dropped', label: 'Students who Dropped' },
  { value: 'Expelled', label: 'Students who were Expelled' },
  { value: 'inactive', label: 'Recent Applicants' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return '—';
  return `${dateObj.toLocaleDateString()} • ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const truncateText = (value, maxLength = 70) => {
  const text = String(value || '').trim();
  if (!text) return 'No message content.';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const toDateValue = (value) => {
  if (!value) return null;
  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
};

const parseDateInputValue = (value, endOfDay = false) => {
  if (!value) return null;
  const parsedDate = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isValidSmsPhoneNumber = (value) => {
  const normalized = String(value || '').trim();
  return normalized.length >= 8;
};

const getStudentGreetingName = (student = {}) => {
  const rawName = String(student.bio || student.fullName || student.name || '').trim();
  if (!rawName) {
    return 'Student';
  }
  return rawName;
};

const getSmsBalanceMeta = (credits) => {
  const numericCredits = Number(credits);

  if (!Number.isFinite(numericCredits)) {
    return {
      tone: 'unknown',
      label: 'Balance unavailable',
      helper: 'We could not read the current SMS credits.',
    };
  }

  if (numericCredits < 50) {
    return {
      tone: 'critical',
      label: 'Critical balance',
      helper: 'Fewer than 50 SMS credits left. Top up as soon as possible.',
    };
  }

  if (numericCredits < 200) {
    return {
      tone: 'warning',
      label: 'Low balance',
      helper: 'Fewer than 200 SMS credits left. Plan a top up soon.',
    };
  }

  return {
    tone: 'healthy',
    label: 'Healthy balance',
    helper: 'Credits are in a comfortable range.',
  };
};

const parseResponseJson = async (response) => {
  const rawText = await response.text();
  let data = {};

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch (error) {
      data = { message: rawText };
    }
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
  }

  return data;
};

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

function SmsComp() {
  const db = getFirestore(app);
  const auth = getAuth();

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [studentsList, setStudentList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [smsStatus, setSmsStatus] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [bulkSending, setBulkSending] = useState(false);
  const [individualSending, setIndividualSending] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('Student');
  const [bulkMessage, setBulkMessage] = useState('');
  const [individualPhoneNumber, setIndividualPhoneNumber] = useState('');
  const [individualMessage, setIndividualMessage] = useState('');
  const [neverJoinedStartDate, setNeverJoinedStartDate] = useState('');
  const [neverJoinedEndDate, setNeverJoinedEndDate] = useState('');
  const [usersIndex, setUsersIndex] = useState([]);
  const [userSearchValue, setUserSearchValue] = useState('');
  const [selectedIndividualUser, setSelectedIndividualUser] = useState(null);
  const [previewSms, setPreviewSms] = useState(null);
  const [smsBalance, setSmsBalance] = useState(null);
  const [smsBalanceLoading, setSmsBalanceLoading] = useState(false);
  const [smsBalanceError, setSmsBalanceError] = useState('');
  const [smsBalanceCheckedAt, setSmsBalanceCheckedAt] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken(true).catch((error) => console.error(error));
      }
    });

    return () => unsubscribe();
  }, [auth]);

  const fetchSmsHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const smsQuery = query(collection(db, 'sentSMS'), orderBy('myTimestamp', 'desc'));
      const querySnapshot = await getDocs(smsQuery);
      const smsItems = [];

      querySnapshot.forEach((smsDoc) => {
        smsItems.push({ id: smsDoc.id, ...smsDoc.data() });
      });

      setStudentList(smsItems);
    } catch (error) {
      setHistoryError(error.message || 'Unable to load sent messages.');
      throw error;
    } finally {
      setHistoryLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchSmsHistory().catch((error) => console.error('Failed to fetch SMS history:', error));
  }, [fetchSmsHistory]);

  const fetchSmsBalance = useCallback(async (options = {}) => {
    const { silent = false } = options;

    if (!silent) {
      setSmsBalanceLoading(true);
    }

    try {
      const response = await fetch(SMS_BALANCE_API_URL);
      const result = await parseResponseJson(response);
      const balanceData = result?.data || {};

      setSmsBalance({
        credits: Number(balanceData.sms_credits ?? 0),
        clientName: balanceData.client_name || 'UBUNIFU COLLEGE',
        statusMessage: balanceData.status_message || '',
      });
      setSmsBalanceError('');
      const checkedAt = new Date().toISOString();
      setSmsBalanceCheckedAt(checkedAt);

      if (typeof window !== 'undefined') {
        const balanceState = {
          credits: Number(balanceData.sms_credits ?? 0),
          clientName: balanceData.client_name || 'UBUNIFU COLLEGE',
          checkedAt,
        };

        window.localStorage.setItem(LEFTBAR_SMS_BALANCE_KEY, JSON.stringify(balanceState));
        window.dispatchEvent(new CustomEvent(LEFTBAR_SMS_BALANCE_EVENT, { detail: balanceState }));
      }
    } catch (error) {
      console.error('Failed to fetch SMS balance:', error);
      setSmsBalanceError(error.message || 'Unable to fetch SMS balance.');
    } finally {
      if (!silent) {
        setSmsBalanceLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchSmsBalance();
  }, [fetchSmsBalance]);

  useEffect(() => {
    const fetchUsersIndex = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = [];

        usersSnapshot.forEach((userDoc) => {
          const data = userDoc.data();
          users.push({
            id: userDoc.id,
            userId: data.userId || userDoc.id,
            bio: data.bio || '',
            userEmail: data.userEmail || '',
            phoneNumber: data.phoneNumber || '',
            course: data.course || '',
            userStatus: data.userStatus || '',
            appDate: data.appDate || null,
          });
        });

        setUsersIndex(users);
      } catch (error) {
        console.error('Failed to load users for SMS search:', error);
      }
    };

    fetchUsersIndex();
  }, [db]);

  const verifySMS = useCallback(async (uniqueId) => {
    if (!uniqueId) {
      return {
        status: 'Unknown',
        statusMessage: 'Missing SMS unique ID.'
      };
    }

    try {
      const response = await fetch(VERIFY_SMS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unique_id: uniqueId })
      });
      const data = await parseResponseJson(response);

      if (data.status === 222) {
        if (data.delivery_status_desc === 'DeliveredToTerminal') {
          return { status: 'DELIVERED', statusMessage: data.delivery_status_desc };
        }

        if (data.delivery_status_desc === 'InvalidMsisdn' || data.delivery_status_desc === 'SenderName Blacklisted') {
          return { status: 'DELIVERY FAILED', statusMessage: data.delivery_status_desc };
        }

        return {
          status: 'PENDING',
          statusMessage: data.delivery_status_desc || 'Pending/Invalid Telco'
        };
      }

      return {
        status: 'Failed',
        statusMessage: data.delivery_status_desc || 'Delivery failed due to unknown reasons.'
      };
    } catch (error) {
      console.error('Error verifying SMS:', error);
      return {
        status: 'Error',
        statusMessage: 'An error occurred while verifying the SMS.'
      };
    }
  }, []);

  const currentItems = useMemo(
    () => studentsList.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage),
    [currentPage, studentsList]
  );

  const pageCount = useMemo(
    () => Math.ceil(studentsList.length / itemsPerPage),
    [studentsList.length]
  );

  useEffect(() => {
    const fetchSmsStatus = async () => {
      const entries = await Promise.all(
        currentItems.map(async (smsItem) => {
          const result = await verifySMS(smsItem.smsUniqueId);
          return [smsItem.id, result];
        })
      );

      setSmsStatus((prevState) => ({
        ...prevState,
        ...Object.fromEntries(entries)
      }));
    };

    if (currentItems.length) {
      fetchSmsStatus();
    }
  }, [currentItems, verifySMS]);

  const deliveredCount = useMemo(
    () => Object.values(smsStatus).filter((item) => item?.status === 'DELIVERED').length,
    [smsStatus]
  );

  const pendingCount = useMemo(
    () => Object.values(smsStatus).filter((item) => item?.status === 'PENDING').length,
    [smsStatus]
  );

  const smsBalanceMeta = useMemo(
    () => getSmsBalanceMeta(smsBalance?.credits),
    [smsBalance]
  );

  const individualUserResults = useMemo(() => {
    const normalizedValue = userSearchValue.trim().toLowerCase();

    if (!normalizedValue) {
      return [];
    }

    return usersIndex
      .filter((user) => {
        const name = String(user.bio || '').toLowerCase();
        const email = String(user.userEmail || '').toLowerCase();
        const course = String(user.course || '').toLowerCase();
        const phone = String(user.phoneNumber || '').toLowerCase();

        return (
          name.includes(normalizedValue) ||
          email.includes(normalizedValue) ||
          course.includes(normalizedValue) ||
          phone.includes(normalizedValue)
        );
      })
      .sort((userA, userB) => {
        const nameA = String(userA.bio || '').toLowerCase();
        const nameB = String(userB.bio || '').toLowerCase();
        const startsA = nameA.startsWith(normalizedValue) ? 0 : 1;
        const startsB = nameB.startsWith(normalizedValue) ? 0 : 1;

        if (startsA !== startsB) {
          return startsA - startsB;
        }

        return nameA.localeCompare(nameB);
      })
      .slice(0, 8);
  }, [userSearchValue, usersIndex]);

  const bulkAudiencePreview = useMemo(() => {
    const startDate = parseDateInputValue(neverJoinedStartDate);
    const endDate = parseDateInputValue(neverJoinedEndDate, true);
    const seenPhoneNumbers = new Set();

    return usersIndex.filter((user) => {
      if (String(user.userStatus || '') !== selectedGroup) {
        return false;
      }

      const phoneNumber = String(user.phoneNumber || '').trim();
      if (!isValidSmsPhoneNumber(phoneNumber)) {
        return false;
      }

      if (selectedGroup !== 'Did Not Join') {
        return true;
      }

      const appliedAt = toDateValue(user.appDate);
      if (!appliedAt) {
        return false;
      }

      if (startDate && appliedAt < startDate) {
        return false;
      }

      if (endDate && appliedAt > endDate) {
        return false;
      }

      if (seenPhoneNumbers.has(phoneNumber)) {
        return false;
      }

      seenPhoneNumbers.add(phoneNumber);
      return true;
    });
  }, [neverJoinedEndDate, neverJoinedStartDate, selectedGroup, usersIndex]);

  const bulkAudienceCount = bulkAudiencePreview.length;
  const BULK_SMS_BATCH_SIZE = 5;

  const getFreshSmsToken = useCallback(async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return '';
    }

    return currentUser.getIdToken(true);
  }, [auth]);

  const sendSmsRequest = useCallback(async ({ phoneNumber, message, authToken }) => {
    const response = await fetch(SMS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`
      },
      body: JSON.stringify({ phoneNumber, message })
    });

    const data = await parseResponseJson(response);

    if (data.status !== 222) {
      const errorMessage = data.status_message || data.error || 'Unknown SMS delivery error.';
      const lowerMessage = String(errorMessage).toLowerCase();

      if (lowerMessage.includes('token') || response.status === 401 || response.status === 403) {
        throw new Error(`Invalid token: ${errorMessage}`);
      }

      throw new Error(errorMessage);
    }

    return data;
  }, []);

  const sendSmsRequestWithRetry = useCallback(async ({ phoneNumber, message, authToken, attempts = 3 }) => {
    let lastError = null;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        return await sendSmsRequest({ phoneNumber, message, authToken });
      } catch (error) {
        lastError = error;
        const errorMessage = String(error?.message || '').toLowerCase();
        const isRetryable = errorMessage.includes('failed to fetch')
          || errorMessage.includes('networkerror')
          || errorMessage.includes('network error')
          || errorMessage.includes('load failed');

        if (!isRetryable || attempt === attempts) {
          throw error;
        }

        await sleep(750 * attempt);
      }
    }

    throw lastError || new Error('Unable to send SMS.');
  }, [sendSmsRequest]);

  const recordSentSms = useCallback(async ({ phoneNumber, message, messageType, smsUniqueId }) => {
    const user = auth.currentUser;
    const newSms = doc(collection(db, 'sentSMS'));

    await setDoc(newSms, {
      phoneNumber,
      smsMessage: message,
      messageType,
      docid: newSms.id,
      myTimestamp: new Date(),
      sentBy: user?.uid || '',
      smsUniqueId
    });
  }, [auth, db]);

  const resolveRecipientByPhoneNumber = useCallback((phoneNumber) => {
    const normalizedPhone = String(phoneNumber || '').trim();

    if (!normalizedPhone) {
      return null;
    }

    return usersIndex.find((user) => String(user.phoneNumber || '').trim() === normalizedPhone) || null;
  }, [usersIndex]);

  const sendBulkSms = async () => {
    if (SMS_SENDING_DISABLED) {
      swal('SMS Disabled', 'Sending SMS is temporarily disabled.', 'info');
      return;
    }

    if (!auth.currentUser) {
      swal('Authentication Error', 'You need to be logged in to send SMS.', 'error');
      return;
    }

    if (!bulkMessage.trim()) {
      swal('Missing Message', 'Please enter the broadcast message first.', 'error');
      return;
    }

    if (selectedGroup === 'Did Not Join' && (!neverJoinedStartDate || !neverJoinedEndDate)) {
      swal('Select Date Range', 'Please choose both the start and end application dates for Never Joined students.', 'error');
      return;
    }

    const startDate = parseDateInputValue(neverJoinedStartDate);
    const endDate = parseDateInputValue(neverJoinedEndDate, true);

    if (selectedGroup === 'Did Not Join' && startDate && endDate && startDate > endDate) {
      swal('Invalid Date Range', 'The start date must come before the end date.', 'error');
      return;
    }

    setBulkSending(true);

    try {
      const recipients = bulkAudiencePreview;
      const authToken = await getFreshSmsToken();

      if (!recipients.length) {
        throw new Error('No valid phone numbers were found for the selected group.');
      }

      const failedRecipients = [];
      let sentCount = 0;

      for (let index = 0; index < recipients.length; index += BULK_SMS_BATCH_SIZE) {
        const batch = recipients.slice(index, index + BULK_SMS_BATCH_SIZE);

        const batchResults = await Promise.allSettled(
          batch.map(async (recipient) => {
            const studentName = getStudentGreetingName(recipient);
            const personalizedMessage = `Hi ${studentName},\n\n${bulkMessage}`;
            const phoneNumber = String(recipient.phoneNumber || '').trim();

            const smsResponse = await sendSmsRequestWithRetry({
              phoneNumber,
              message: personalizedMessage,
              authToken
            });

            await recordSentSms({
              phoneNumber,
              message: personalizedMessage,
              messageType: 'Broadcast',
              smsUniqueId: smsResponse.unique_id
            });

            return { phoneNumber };
          })
        );

        const batchSentCount = batchResults.filter((result) => result.status === 'fulfilled').length;
        sentCount += batchSentCount;

        batchResults.forEach((result, batchIndex) => {
          if (result.status === 'fulfilled') return;

          const failedRecipient = batch[batchIndex];
          failedRecipients.push({
            name: getStudentGreetingName(failedRecipient),
            phoneNumber: String(failedRecipient.phoneNumber || '').trim(),
            reason: result.reason?.message || 'Unknown error'
          });
        });
      }

      await fetchSmsHistory();
      await fetchSmsBalance({ silent: true });
      setBulkModalOpen(false);
      setBulkMessage('');
      setNeverJoinedStartDate('');
      setNeverJoinedEndDate('');

      logAdminActivity({
        action: 'Send SMS',
        details: `Broadcast SMS to ${selectedGroup} students`,
        meta: {
          page: 'SMS',
          messageType: 'Broadcast',
          targetGroup: selectedGroup,
          recipientCount: recipients.length,
          sentCount,
          failedCount: failedRecipients.length,
          startDate: selectedGroup === 'Did Not Join' ? neverJoinedStartDate : '',
          endDate: selectedGroup === 'Did Not Join' ? neverJoinedEndDate : ''
        }
      });

      if (failedRecipients.length > 0) {
        swal(
          'Partially Sent',
          `Sent ${sentCount} of ${recipients.length} messages. ${failedRecipients.length} failed, likely due to temporary network/API issues.`,
          'warning'
        );
        return;
      }

      swal('Success', 'All SMS messages have been sent successfully.', 'success');
    } catch (error) {
      console.error('Error sending bulk SMS:', error);
      swal('Error', `An error occurred while sending SMS: ${error.message}`, 'error');
    } finally {
      setBulkSending(false);
    }
  };

  const sendIndividualSMS = async () => {
    if (SMS_SENDING_DISABLED) {
      swal('SMS Disabled', 'Sending SMS is temporarily disabled.', 'info');
      return;
    }

    if (!auth.currentUser) {
      swal('Authentication Error', 'You need to be logged in to send SMS.', 'error');
      return;
    }

    if (!individualPhoneNumber.trim() || !individualMessage.trim()) {
      swal('Missing Details', 'Enter both the phone number and the message.', 'error');
      return;
    }

    setIndividualSending(true);

    try {
      const authToken = await getFreshSmsToken();
      const recipient = selectedIndividualUser || resolveRecipientByPhoneNumber(individualPhoneNumber);
      const studentName = getStudentGreetingName(recipient || { bio: individualPhoneNumber.trim() });
      const personalizedMessage = `Hi ${studentName},\n\n${individualMessage}`;
      const smsResponse = await sendSmsRequest({
        phoneNumber: individualPhoneNumber.trim(),
        message: personalizedMessage,
        authToken
      });

      await recordSentSms({
        phoneNumber: individualPhoneNumber.trim(),
        message: personalizedMessage,
        messageType: 'Individual',
        smsUniqueId: smsResponse.unique_id
      });

      await fetchSmsHistory();
      await fetchSmsBalance({ silent: true });
      setIndividualModalOpen(false);
      setIndividualPhoneNumber('');
      setIndividualMessage('');
      setUserSearchValue('');
      setSelectedIndividualUser(null);

      logAdminActivity({
        action: 'Send SMS',
        details: `Sent individual SMS to ${individualPhoneNumber.trim()}`,
        meta: { page: 'SMS', messageType: 'Individual', phoneNumber: individualPhoneNumber.trim() }
      });

      swal('SMS Sent', 'Your SMS has been sent successfully.', 'success');
    } catch (error) {
      console.error('Error sending individual SMS:', error);
      swal('SMS not Sent', `Your SMS was not sent: ${error.message}`, 'error');
    } finally {
      setIndividualSending(false);
    }
  };

  const handleSelectIndividualUser = (user) => {
    setSelectedIndividualUser(user);
    setUserSearchValue(user.bio || user.userEmail || user.phoneNumber || '');
    setIndividualPhoneNumber(String(user.phoneNumber || '').trim());
  };

  const handleCloseIndividualModal = () => {
    setIndividualModalOpen(false);
    setUserSearchValue('');
    setSelectedIndividualUser(null);
  };

  const clearSelectedIndividualUser = () => {
    setSelectedIndividualUser(null);
    setUserSearchValue('');
  };

  return (
    <div className="clientsComp applicationsPage smsPage">
      <header className="smsPageHeader">
        <div>
          <p className="smsPageEyebrow">COMMUNICATIONS</p>
          <h1>SMS messages</h1>
          <p>Send updates and review delivery history.</p>
        </div>
        <div className="smsPageStats" aria-label="SMS overview">
          <div className="smsPageStat">
            <span>Messages</span>
            <strong>{studentsList.length}</strong>
          </div>
          <div className="smsPageStat">
            <span>Delivered</span>
            <strong>{deliveredCount}</strong>
          </div>
          <div className="smsPageStat">
            <span>Pending</span>
            <strong>{pendingCount}</strong>
          </div>
          <div className={`smsPageStat smsPageStat--balance smsPageStat--${smsBalanceError ? 'unknown' : smsBalanceMeta.tone}`}>
            <span>Credits remaining</span>
            <strong>{smsBalanceLoading && !smsBalance ? '...' : (smsBalance?.credits ?? 0)}</strong>
          </div>
        </div>
      </header>

      <div className="appsFiltersCard smsActionBar">
        <div className="smsActionIntro">
          <div className="smsActionIcon">
            <FontAwesomeIcon icon={faCommentSms} />
          </div>
          <div>
              <strong>Choose how to send</strong>
            <span>
              Send an update to a group or an individual.
              {' '}
              {smsBalance?.clientName || 'UBUNIFU COLLEGE'} has {smsBalance?.credits ?? 0} SMS credits.
            </span>
            {!smsBalanceError && smsBalanceMeta.tone !== 'healthy' ? (
              <span className={`smsBalanceIndicator smsBalanceIndicator--${smsBalanceMeta.tone}`}>
                {smsBalanceMeta.label}: {smsBalanceMeta.helper}
              </span>
            ) : null}
            <span>
              {smsBalanceError
                ? `Balance check failed: ${smsBalanceError}`
                : `Last checked: ${formatDateTime(smsBalanceCheckedAt)}`}
            </span>
          </div>
        </div>
        <div className="smsActionButtons">
          <button className="appsViewBtn" onClick={() => fetchSmsBalance()} disabled={smsBalanceLoading}>
            <span>{smsBalanceLoading ? 'Checking balance...' : 'Refresh balance'}</span>
          </button>
          <button className="interviewScheduleBtn" onClick={() => setBulkModalOpen(true)}>
            <FontAwesomeIcon icon={faPaperPlane} />
            <span>Broadcast to group</span>
          </button>
          <button className="interviewCallBtn" onClick={() => setIndividualModalOpen(true)}>
            <FontAwesomeIcon icon={faUser} />
            <span>Send individual SMS</span>
          </button>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <div className="smsHistoryHeading">
            <h6>Sent message history</h6>
            <p>Recent messages and delivery updates</p>
          </div>
          <div className="smsHistoryHeaderActions">
            <span>{historyLoading ? 'Loading…' : historyError ? 'Unavailable' : `${studentsList.length} messages`}</span>
            <button type="button" onClick={() => fetchSmsHistory().catch((error) => console.error(error))} disabled={historyLoading}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Refresh
            </button>
          </div>
        </div>
        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>To</th>
                <th>Type</th>
                <th>Message</th>
                <th>Status</th>
                <th>Status Message</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading && (
                <tr><td colSpan="6" className="smsHistoryLoading"><Spinner animation="border" size="sm" /> Loading sent messages…</td></tr>
              )}
              {!historyLoading && !historyError && currentItems.map((smsItem) => (
                <tr key={smsItem.id}>
                  <td>
                    <div className="smsHistoryDate" title={formatDateTime(smsItem.myTimestamp)}>
                      <strong>{toDateValue(smsItem.myTimestamp)?.toLocaleDateString() || '—'}</strong>
                      <span>{toDateValue(smsItem.myTimestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || ''}</span>
                    </div>
                  </td>
                  <td>
                    <div className="smsHistoryRecipient">
                      {smsItem.studentName && <strong>{smsItem.studentName}</strong>}
                      <MaskedPhone
                        phoneNumber={smsItem.phoneNumber}
                        studentId={smsItem.userId}
                        studentName={smsItem.studentName}
                      />
                    </div>
                  </td>
                  <td><span className="smsTypeBadge">{smsItem.messageType || 'SMS'}</span></td>
                  <td>
                    <button
                      type="button"
                      className="smsMessagePreview"
                      onClick={() => setPreviewSms(smsItem)}
                    >
                      <span className="smsMessagePreviewText">
                        {truncateText(smsItem.smsMessage)}
                      </span>
                      <span className="smsMessagePreviewAction">Read full SMS</span>
                    </button>
                  </td>
                  <td>
                    {smsStatus[smsItem.id] ? (
                      <span className={`smsDeliveryBadge smsDeliveryBadge--${String(smsStatus[smsItem.id].status || 'unknown').toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                        {smsStatus[smsItem.id].status || 'Unknown'}
                      </span>
                    ) : <span className="smsDeliveryPending"><Spinner animation="border" size="sm" /> Checking</span>}
                  </td>
                  <td><span className="smsStatusMessage">{smsStatus[smsItem.id]?.statusMessage || 'Waiting for delivery update'}</span></td>
                </tr>
              ))}
              {!historyLoading && historyError && (
                <tr><td colSpan="6" className="smsHistoryError">Couldn’t load message history. Refresh to try again.</td></tr>
              )}
              {!historyLoading && !historyError && currentItems.length === 0 && (
                <tr>
                  <td colSpan="6" className="appsEmptyState">
                    No SMS messages have been sent yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {!historyLoading && !historyError && pageCount > 1 && (
          <div className="communicationsPaginationWrap">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={({ selected }) => setCurrentPage(selected)}
              containerClassName="pagination justify-content-center"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakClassName="page-item"
              breakLinkClassName="page-link"
              activeClassName="active"
            />
          </div>
        )}
      </div>

      <Modal show={bulkModalOpen} onHide={() => setBulkModalOpen(false)} centered contentClassName="smsModalContent">
        <Modal.Header closeButton>
          <Modal.Title>Broadcast SMS</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Group Type</Form.Label>
            <Form.Select value={selectedGroup} onChange={(event) => setSelectedGroup(event.target.value)}>
              {SMS_GROUP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedGroup === 'Did Not Join' && (
            <div className="smsNeverJoinedRange">
              <Form.Label>Application Date Range</Form.Label>
              <div className="smsNeverJoinedRangeGrid">
                <Form.Group className="mb-3">
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="date"
                    value={neverJoinedStartDate}
                    onChange={(event) => setNeverJoinedStartDate(event.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="date"
                    value={neverJoinedEndDate}
                    onChange={(event) => setNeverJoinedEndDate(event.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="smsUserSearchHint">
                Choose the application period for Never Joined students. The range is inclusive.
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Your message here"
              style={{ height: '120px' }}
              value={bulkMessage}
              onChange={(event) => setBulkMessage(event.target.value)}
            />
          </Form.Group>

          <div className="smsUserSearchHint">
            Eligible recipients: {bulkAudienceCount}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setBulkModalOpen(false)} disabled={bulkSending}>
            Close
          </Button>
          <Button variant="dark" onClick={sendBulkSms} disabled={SMS_SENDING_DISABLED || bulkSending || bulkAudienceCount === 0}>
            {SMS_SENDING_DISABLED ? 'Send disabled' : (bulkSending ? 'Sending...' : 'Send')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={individualModalOpen} onHide={handleCloseIndividualModal} centered contentClassName="smsModalContent">
        <Modal.Header closeButton>
          <Modal.Title>Send SMS to an Individual</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search User</Form.Label>
            <div className="smsUserSearchField">
              <FontAwesomeIcon icon={faSearch} className="smsUserSearchIcon" />
              <Form.Control
                type="text"
                placeholder="Search by name, email, course, or phone"
                value={userSearchValue}
                onChange={(event) => {
                  setUserSearchValue(event.target.value);
                  if (selectedIndividualUser) {
                    setSelectedIndividualUser(null);
                  }
                }}
              />
            </div>
            <div className="smsUserSearchHint">You can search a user or type a phone number directly below.</div>

            {selectedIndividualUser ? (
              <div className="smsSelectedUserCard">
                <div>
                  <strong>{selectedIndividualUser.bio || 'Unnamed user'}</strong>
                  <span>{selectedIndividualUser.userEmail || selectedIndividualUser.course || 'No extra details'}</span>
                  <small>
                    {selectedIndividualUser.userStatus || 'Status unavailable'}
                    {' • '}
                    Admission: {formatDateTime(selectedIndividualUser.appDate)}
                  </small>
                  <small>{selectedIndividualUser.phoneNumber || 'No phone number recorded'}</small>
                </div>
                <button type="button" className="smsSelectedUserClear" onClick={clearSelectedIndividualUser}>
                  Change
                </button>
              </div>
            ) : null}

            {!selectedIndividualUser && individualUserResults.length > 0 ? (
              <div className="smsUserSearchResults">
                {individualUserResults.map((user) => (
                  <button
                    type="button"
                    key={user.userId}
                    className="smsUserSearchResult"
                    onClick={() => handleSelectIndividualUser(user)}
                  >
                    <strong>{user.bio || 'Unnamed user'}</strong>
                    <span>{user.userEmail || user.course || 'No email recorded'}</span>
                    <small>
                      {user.userStatus || 'Status unavailable'}
                      {' • '}
                      Admission: {formatDateTime(user.appDate)}
                    </small>
                    <small>{user.phoneNumber || 'No phone number recorded'}</small>
                  </button>
                ))}
              </div>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter phone number or use search above"
              value={individualPhoneNumber}
              onChange={(event) => setIndividualPhoneNumber(event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Your message here"
              style={{ height: '120px' }}
              value={individualMessage}
              onChange={(event) => setIndividualMessage(event.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleCloseIndividualModal} disabled={individualSending}>
            Close
          </Button>
          <Button variant="dark" onClick={sendIndividualSMS} disabled={SMS_SENDING_DISABLED || individualSending}>
            {SMS_SENDING_DISABLED ? 'Send disabled' : (individualSending ? 'Sending...' : 'Send')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(previewSms)} onHide={() => setPreviewSms(null)} centered contentClassName="smsModalContent">
        <Modal.Header closeButton>
          <Modal.Title>SMS Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewSms ? (
            <div className="smsPreviewModalBody">
              <div className="smsPreviewMeta">
                <div>
                  <span>Sent</span>
                  <strong>{formatDateTime(previewSms.myTimestamp)}</strong>
                </div>
                <div>
                  <span>Type</span>
                  <strong>{previewSms.messageType || '—'}</strong>
                </div>
                <div>
                  <span>To</span>
                  <strong>{previewSms.phoneNumber || '—'}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{smsStatus[previewSms.id]?.status || 'Loading...'}</strong>
                </div>
              </div>
              <div className="smsPreviewMessageCard">
                <p>{previewSms.smsMessage || 'No message content.'}</p>
              </div>
              <div className="smsPreviewStatusNote">
                {smsStatus[previewSms.id]?.statusMessage || 'Loading delivery details...'}
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setPreviewSms(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SmsComp;
